import { AlertTriangle } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { useLanguage } from "@/shared/hooks/use-language";
import { useNumberFormat } from "@/shared/hooks/use-number-format";
import type { NumberFormat } from "@/shared/lib/user-setting";
import { bookingCategoriesInOrder, bookingCountLabel } from "@/features/portfolio/lib/booking-label";
import {
  bookingStateOf,
  type BookingCounts,
  type BookingState,
  type BookingStatus,
} from "@/features/portfolio/hooks/use-booking-status";

interface BookingPart {
  testId: string;
  text: string;
  /** Opens a sentence of its own rather than continuing the comma-joined list of what
   *  the book is waiting on — the unchecked-imports caveat, which qualifies that list
   *  instead of extending it. */
  opensSentence: boolean;
}

/** What the sentence names, in the order `bookingCategoriesInOrder` gives it — the same
 *  priority `emptyPositionsCopy` cascades through, so the two can never disagree on which
 *  category a book is actually waiting on. */
function bookingParts(
  t: (key: string) => string,
  counts: BookingCounts,
  numberFormat: NumberFormat,
): BookingPart[] {
  return bookingCategoriesInOrder(counts).map((category) => {
    switch (category) {
      case "trades":
        return {
          testId: "booking-trade-count",
          text: bookingCountLabel(
            t, "portfolio.booking.trades", counts.unbookedTradeCount, counts.isPartial, numberFormat,
          ),
          opensSentence: false,
        };
      case "snapshots":
        return {
          testId: "booking-snapshot-count",
          text: bookingCountLabel(
            t, "portfolio.booking.snapshots", counts.unsettledSnapshotCount, counts.isPartial, numberFormat,
          ),
          opensSentence: false,
        };
      case "imports":
        return {
          testId: "booking-import-count",
          text: bookingCountLabel(
            t, "portfolio.booking.imports", counts.runningImportCount, counts.isPartial, numberFormat,
          ),
          opensSentence: false,
        };
      case "uncounted":
        // Every count is zero, yet `isSettlingCounts` still called this a settling
        // book: some read carried a cursor, so the zero is an unproven lower bound.
        return { testId: "booking-uncounted", text: t("portfolio.booking.uncounted"), opensSentence: false };
      case "importsUnchecked":
        // A running-import read failed this tick, so the running-import figure is
        // missing whatever it would have returned — said as its own sentence after
        // whatever the book is known to be waiting on, or alone when nothing is.
        return {
          testId: "booking-imports-unchecked",
          text: t("portfolio.booking.importsUnchecked"),
          opensSentence: true,
        };
    }
  });
}

/** The text that precedes `part` when it is not the first: a comma continues the list
 *  of what the book is waiting on, a period opens the caveat's own sentence. */
function joinerBefore(part: BookingPart): string {
  return part.opensSentence ? ". " : ", ";
}

/** The compact chip's short label, one per state — each must stop implying the poll is
 *  still running the moment it no longer is. */
const SHORT_LABEL_KEY: Record<BookingState, string> = {
  settling: "portfolio.booking.short",
  slow: "portfolio.booking.shortSlow",
  "timed-out": "portfolio.booking.shortTimedOut",
  unknown: "portfolio.booking.shortUnknown",
  incomplete: "portfolio.booking.shortIncomplete",
};

interface BookingMarkerProps {
  status: BookingStatus;
  /** `compact`: icon and a short label, the full sentence as `aria-label` — for beside a
   *  figure. `full`: icon, the sentence, and Check again where a state offers one. */
  size: "compact" | "full";
  /** Which figure a `compact` marker sits beside — `NavHeader` renders two on the same
   *  screen (NAV and position count), so this suffixes the testid
   *  (`booking-marker-compact-nav` / `-count`) to keep each one a unique, targetable
   *  element instead of both sharing `booking-marker-compact`. Unused for `full`, which
   *  renders once per page. */
  placement?: "nav" | "count";
}

/**
 * Says that the figures on the page are not final yet, and why. Rendered wherever a
 * reader could otherwise take an empty table or an unchanged NAV as the last word after
 * an import or a declaration: the NAV and position-count cards (compact) and above the
 * positions table (full). Renders nothing while the book is settled.
 *
 * The console's warning idiom, as the pricing page draws it: `AlertTriangle` in yellow,
 * text in the matching yellow — never the destructive red, because nothing here has
 * failed. Even the unknown and incomplete states are a failed *check*, not a failed
 * booking.
 *
 * Every state past plain settling offers a way out: the compact chip itself becomes the
 * control (a `<button>` calling `check()`, with the full sentence as its `aria-label`) so
 * every screen the chip appears on — including one with no full marker in sight — has an
 * exit, and the full marker keeps its own Check again button beside the sentence. Only
 * `settling` renders as plain, non-interactive text; there is nothing to check again yet.
 *
 * `incomplete` is the check that answered but could not verify the running imports
 * (`useBookingStatus`'s `isIncomplete`): the sentence is the unchecked-imports caveat
 * alone, and its tail — like the timed-out one — must never promise an auto-refresh,
 * because no poll runs on a book with nothing known to be settling. A settling book with
 * the same failed read is not this state: it keeps its settling copy and cadence, with
 * the caveat appended after its counts.
 *
 * Only the full marker carries a live region — a visually hidden span announcing the
 * state's own copy, without counts, so a screen-reader user hears one transition per
 * state change instead of the count-bearing sentence repeating every poll tick. The
 * visible sentence and the compact chips are plain text; `aria-busy` no longer flips per
 * tick anywhere in this component.
 */
export const BookingMarker = ({ status, size, placement }: BookingMarkerProps) => {
  const { t } = useLanguage();
  const { numberFormat } = useNumberFormat();
  const state = bookingStateOf(status);
  if (state == null) return null;

  const parts = status.counts ? bookingParts(t, status.counts, numberFormat) : [];
  // `timed-out` and `incomplete` must never reuse the settling/slow tail: both of those
  // still promise an auto-refresh (`emptyPositionsCopy`'s own doc comment on the sibling
  // empty-table copy states this rule), and these two are exactly the states where
  // `refetchInterval` returns `false` — polling has stopped, or never started — a claim
  // next to the Check again button that exists because the page will not check on its
  // own.
  const tail = state === "settling"
    ? t("portfolio.booking.tail")
    : state === "timed-out"
      ? t("portfolio.booking.stopped")
      : state === "incomplete"
        ? t("portfolio.booking.incompleteTail")
        : t("portfolio.booking.slow");
  const listed = parts
    .map((part, index) => (index > 0 ? joinerBefore(part) : "") + part.text)
    .join("");
  const sentence = state === "unknown"
    ? t("portfolio.booking.unknown")
    : `${listed}. ${tail}`;
  const showsCheckAgain = state !== "settling";
  // `incomplete` announces its full sentence too, not just the tail: the caveat
  // (`importsUnchecked`) is the actual reason for the state, and unlike settling/slow —
  // which repeat on every poll tick, so the live region strips counts to say one
  // transition per state change — `incomplete` never re-ticks, so there is no repeat-
  // announcement cost to including it here.
  const announcement = state === "unknown" || state === "incomplete" ? sentence : tail;

  if (size === "compact") {
    const shortLabel = t(SHORT_LABEL_KEY[state]);
    const content = (
      <>
        <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" aria-hidden="true" />
        <span>{shortLabel}</span>
      </>
    );
    const className = "inline-flex items-center gap-1 text-xs font-medium text-yellow-700 dark:text-yellow-400";
    // `NavHeader` renders one of these beside the NAV and one beside the position count
    // on the same screen — the suffix keeps each a unique, targetable element instead of
    // both sharing `booking-marker-compact` (a `get_by_test_id` strict-mode violation).
    const testId = placement ? `booking-marker-compact-${placement}` : "booking-marker-compact";
    if (showsCheckAgain) {
      return (
        <button
          type="button"
          onClick={status.check}
          aria-label={sentence}
          className={`${className} bg-transparent border-0 p-0 cursor-pointer`}
          data-testid={testId}
          data-booking-state={state}
        >
          {content}
        </button>
      );
    }
    return (
      <span
        aria-label={sentence}
        className={className}
        data-testid={testId}
        data-booking-state={state}
      >
        {content}
      </span>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3"
      data-testid="booking-marker-full"
      data-booking-state={state}
    >
      {/* The one live region for every booking marker on the page — see the component
          doc comment above. */}
      <span role="status" aria-live="polite" className="sr-only">{announcement}</span>
      <div className="flex items-start gap-2 text-sm text-yellow-700 dark:text-yellow-400">
        <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" aria-hidden="true" />
        <p>
          {state === "unknown" ? (
            sentence
          ) : (
            <>
              {parts.map((part, index) => (
                <span key={part.testId}>
                  {index > 0 && joinerBefore(part)}
                  <span data-testid={part.testId}>{part.text}</span>
                </span>
              ))}
              {". "}
              {tail}
            </>
          )}
        </p>
      </div>
      {showsCheckAgain && (
        <Button variant="outline" size="sm" onClick={status.check} data-testid="booking-check-again">
          {t("portfolio.booking.checkAgain")}
        </Button>
      )}
    </div>
  );
};
